import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Loader2,
  MessageSquare,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import logoRw from '../logo-rw.png';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { assessmentsApi } from '../lib/api-client';
import type { PublicAssessmentDetailsDto } from '../types/api';

const DECISION_LABELS: Record<string, string> = {
  COMPLIANT: 'Compliant',
  NON_COMPLIANT: 'Non-Compliant',
  NO_ACTION: 'No Action',
  WARNING: 'Warning',
  FINES: 'Fines',
  CLOSURE: 'Closure',
};

const DECISION_COLORS: Record<string, string> = {
  COMPLIANT: 'bg-green-100 text-green-800',
  NON_COMPLIANT: 'bg-red-100 text-red-800',
  NO_ACTION: 'bg-gray-100 text-gray-700',
  WARNING: 'bg-yellow-100 text-yellow-800',
  FINES: 'bg-orange-100 text-orange-800',
  CLOSURE: 'bg-red-100 text-red-800',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function AssessmentDetails({ details }: { details: PublicAssessmentDetailsDto }) {
  const [showPhotos, setShowPhotos] = useState(false);

  const locationLabel = details.locationStack
    ? details.locationStack.map((l) => l.name).join(' › ')
    : null;

  return (
    <Card className='w-full'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex items-center gap-2'>
            <Building2 className='h-5 w-5 text-primary flex-shrink-0' />
            <div>
              <CardTitle className='text-base'>{details.facility?.title ?? 'Unknown Facility'}</CardTitle>
              {locationLabel && <p className='text-xs text-gray-500 mt-0.5'>{locationLabel}</p>}
            </div>
          </div>
          <span
            className={`text-sm font-semibold px-3 py-1 rounded-md ${details.isClean ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
          >
            {details.percentageScore}%
          </span>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Meta row */}
        <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600'>
          <span className='flex items-center gap-1.5'>
            <Calendar className='h-4 w-4 text-gray-400' />
            {formatDate(details.createdAt)}
          </span>
          {details.carriedBy && (
            <span className='flex items-center gap-1.5'>
              <User className='h-4 w-4 text-gray-400' />
              {details.carriedBy.name}
            </span>
          )}
          <span className='flex items-center gap-1.5'>
            <ClipboardList className='h-4 w-4 text-gray-400' />
            {details.assessmentType === 'HYGIENE' ? 'Hygiene Assessment' : 'Compliance Assessment'}
          </span>
        </div>

        {/* Decisions */}
        {details.decisions.length > 0 && (
          <div>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>Decision(s)</p>
            <div className='flex flex-wrap gap-2'>
              {details.decisions.map((d) => (
                <span
                  key={d}
                  className={`text-xs font-medium px-2.5 py-1 rounded-md ${DECISION_COLORS[d] ?? 'bg-gray-100 text-gray-700'}`}
                >
                  {DECISION_LABELS[d] ?? d}
                </span>
              ))}
              {details.chargedAmount != null && details.chargedAmount > 0 && (
                <span className='text-xs font-medium px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 border border-orange-200'>
                  {details.chargedAmount.toLocaleString()} RWF
                </span>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {details.recommendations.length > 0 && (
          <div>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
              Recommendations ({details.recommendations.length})
            </p>
            <ul className='space-y-1.5'>
              {details.recommendations.map((r) => (
                <li key={r.id} className='flex items-start gap-2 text-sm text-gray-700'>
                  <span className='mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0' />
                  <span>
                    {r.recommendation}
                    {r.deadline && (
                      <span className='ml-1.5 text-xs text-gray-400'>
                        (by {formatDate(r.deadline)})
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Additional comment */}
        {details.additionalComment && (
          <div className='p-3 bg-gray-50 rounded-md border border-gray-200'>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1'>Inspector Note</p>
            <p className='text-sm text-gray-700'>{details.additionalComment}</p>
          </div>
        )}

        {/* Committee */}
        {details.committee?.length > 0 && (
          <div>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>Committee</p>
            <div className='flex flex-wrap gap-1.5'>
              {details.committee.map((m, i) => (
                <span key={i} className='text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded'>
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hygiene Q&A sections */}
        {details.assessmentType === 'HYGIENE' && (details.hygieneSections?.length ?? 0) > 0 && (
          <div>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-2'>Assessment Questions</p>
            <div className='space-y-3'>
              {(details.hygieneSections ?? []).map((section) => (
                <div key={section.id} className='border border-gray-200 rounded-md overflow-hidden'>
                  <div className='bg-gray-50 px-3 py-2 flex items-center justify-between'>
                    <p className='text-xs font-semibold text-gray-700 uppercase tracking-wide'>{section.name}</p>
                    <span className='text-xs text-gray-500'>
                      {section.questions.reduce((s, q) => s + q.marks, 0)}/
                      {section.questions.reduce((s, q) => s + q.weight, 0)} pts
                    </span>
                  </div>
                  <div className='divide-y divide-gray-100'>
                    {section.questions.map((q, i) => (
                      <div key={i} className='px-3 py-2 flex items-start justify-between gap-3'>
                        <p className='text-xs text-gray-700 flex-1'>{q.question}</p>
                        <div className='flex items-center gap-2 flex-shrink-0'>
                          <span
                            className={`text-xs px-2 py-0.5 rounded font-medium ${
                              q.marks === q.weight
                                ? 'bg-green-50 text-green-700'
                                : q.marks > 0
                                  ? 'bg-yellow-50 text-yellow-700'
                                  : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {q.selectedOption}
                          </span>
                          <span className='text-xs text-gray-400 w-12 text-right'>
                            {q.marks}/{q.weight}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Q&A */}
        {details.assessmentType === 'COMPLIANCE' && (details.complianceAnswers?.length ?? 0) > 0 && (
          <div>
            <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-2'>Compliance Checklist</p>
            <div className='border border-gray-200 rounded-md divide-y divide-gray-100 overflow-hidden'>
              {(details.complianceAnswers ?? []).map((q, i) => (
                <div key={i} className='px-3 py-2 flex items-start justify-between gap-3'>
                  <p className='text-xs text-gray-700 flex-1'>{q.question}</p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${
                      q.marks > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {q.selectedOption}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos toggle */}
        {details.assessmentPhotos.length > 0 && (
          <div>
            <button
              type='button'
              onClick={() => setShowPhotos((v) => !v)}
              className='flex items-center gap-1 text-xs font-medium text-primary hover:underline'
            >
              {showPhotos ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />}
              {showPhotos ? 'Hide' : 'Show'} photos ({details.assessmentPhotos.length})
            </button>
            {showPhotos && (
              <div className='mt-2 grid grid-cols-2 gap-2'>
                {details.assessmentPhotos.map((p) => (
                  <img
                    key={p.id}
                    src={p.url}
                    alt='Assessment photo'
                    className='w-full h-32 object-cover rounded-md border border-gray-200'
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AppealPage() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [details, setDetails] = useState<PublicAssessmentDetailsDto | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!assessmentId) return;
    assessmentsApi
      .getPublicDetails(assessmentId)
      .then(setDetails)
      .catch(() => setDetailsError('Could not load assessment details.'))
      .finally(() => setLoadingDetails(false));
  }, [assessmentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assessmentId || !reason.trim()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await assessmentsApi.submitAppeal(assessmentId, reason.trim());
      setSubmitted(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to submit appeal. Please try again.';
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Block if there's already a pending or accepted appeal
  if (!loadingDetails && details?.existingAppeal) {
    const isPending = details.existingAppeal.status === 'PENDING';
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 gap-6'>
        <div className='flex flex-col items-center text-center gap-1.5'>
          <img src={logoRw} alt='Republic of Rwanda' className='h-14 w-14' />
          <p className='text-xs font-semibold uppercase tracking-widest text-gray-500'>Republic of Rwanda</p>
          <p className='text-sm font-bold text-gray-900'>Ministry of Health</p>
        </div>
        <Card className='w-full max-w-lg'>
          <CardContent className='pt-8 pb-8 text-center'>
            {isPending ? (
              <Clock className='h-12 w-12 text-yellow-500 mx-auto mb-4' />
            ) : (
              <ShieldCheck className='h-12 w-12 text-green-500 mx-auto mb-4' />
            )}
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>
              {isPending ? 'Appeal Already Submitted' : 'Appeal Accepted'}
            </h2>
            <p className='text-sm text-gray-600'>
              {isPending
                ? 'An appeal for this assessment is already pending review. You will be notified once a decision has been made.'
                : 'The appeal for this assessment has already been accepted and is being processed.'}
            </p>
            <p className='text-xs text-gray-400 mt-3'>
              Submitted on {formatDate(details.existingAppeal.createdAt)}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 gap-6'>
        <div className='flex flex-col items-center text-center gap-1.5'>
          <img src={logoRw} alt='Republic of Rwanda' className='h-14 w-14' />
          <p className='text-xs font-semibold uppercase tracking-widest text-gray-500'>Republic of Rwanda</p>
          <p className='text-sm font-bold text-gray-900'>Ministry of Health</p>
        </div>
        <Card className='w-full max-w-lg'>
          <CardContent className='pt-8 pb-8 text-center'>
            <CheckCircle2 className='h-12 w-12 text-green-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>Appeal Submitted</h2>
            <p className='text-sm text-gray-600'>
              Your appeal has been received. The responsible administrators have been notified and will review your
              case.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 p-4'>
      {/* Header */}
      <div className='w-full max-w-lg mx-auto mb-6'>
        <div className='flex flex-col items-center text-center gap-2 py-6'>
          <img src={logoRw} alt='Republic of Rwanda' className='h-16 w-16' />
          <div>
            <p className='text-xs font-semibold uppercase tracking-widest text-gray-500'>Republic of Rwanda</p>
            <h1 className='text-base font-bold text-gray-900 leading-tight mt-0.5'>Ministry of Health</h1>
            <p className='text-xs text-gray-500 mt-0.5'>National Hygiene and Sanitation Inspection Platform</p>
          </div>
        </div>
        <div className='h-px bg-gray-200' />
      </div>

      <div className='w-full max-w-lg mx-auto space-y-4'>
        {/* Assessment details */}
        {loadingDetails ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-6 w-6 animate-spin text-primary' />
          </div>
        ) : detailsError ? (
          <div className='flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md'>
            <AlertCircle className='h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0' />
            <p className='text-sm text-yellow-700'>{detailsError}</p>
          </div>
        ) : details ? (
          <AssessmentDetails details={details} />
        ) : null}

        {/* Appeal form */}
        <Card className='w-full'>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <MessageSquare className='h-5 w-5 text-primary' />
              <CardTitle>Submit an Appeal</CardTitle>
            </div>
            <p className='text-sm text-gray-600 mt-1'>
              Dispute the findings of your facility assessment. Provide a clear explanation below.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className='space-y-4'>
              <div>
                <label htmlFor='reason' className='block text-sm font-medium text-gray-700 mb-1'>
                  Reason for appeal <span className='text-red-500'>*</span>
                </label>
                <textarea
                  id='reason'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder='Explain why you are disputing this assessment result...'
                  rows={5}
                  className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none'
                  disabled={isSubmitting}
                />
                {!reason.trim() && (
                  <p className='mt-1 text-xs text-gray-400'>Please provide a reason before submitting.</p>
                )}
              </div>

              {submitError && (
                <div className='flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md'>
                  <AlertCircle className='h-4 w-4 text-red-500 mt-0.5 flex-shrink-0' />
                  <p className='text-sm text-red-700'>{submitError}</p>
                </div>
              )}

              <Button type='submit' disabled={isSubmitting || !reason.trim()} className='w-full'>
                {isSubmitting ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Submitting...
                  </>
                ) : (
                  'Submit Appeal'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
